import React, { useMemo, type FC } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import PageSection from 'src/components/page/PageSection';

type Props = {
    currentDate: Dayjs;
    weekdayLabels: string[];
    activityDaySet: Set<string>;
};

const MonthlyCalendarSection: FC<Props> = ({
    currentDate,
    weekdayLabels,
    activityDaySet,
}) => {
    const [displayedDate, setDisplayedDate] = React.useState<Dayjs>(() => currentDate);
    const now = dayjs();
    const cutoffDate = now.subtract(60, 'day');
    const monthLabel = displayedDate.format('MMMM YYYY');
    const todayKey = now.format('YYYY-MM-DD');
    const isCurrentMonth = displayedDate.isSame(now, 'month');
    const previousMonthEnd = displayedDate.subtract(1, 'month').endOf('month');
    const isLeftChevronDisabled = previousMonthEnd.isBefore(cutoffDate, 'day');

    const monthGrid = useMemo(() => {
        const monthStart = displayedDate.startOf('month');
        const daysInMonth = displayedDate.daysInMonth();
        const startOffset = (monthStart.day() + 6) % 7;
        const cells: Array<Dayjs | null> = [];

        for (let i = 0; i < startOffset; i++) {
            cells.push(null);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            cells.push(monthStart.date(day));
        }

        while (cells.length % 7 !== 0) {
            cells.push(null);
        }

        return cells;
    }, [displayedDate]);

    return (
        <PageSection variant="outlined">
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <IconButton
                    size="small"
                    disabled={isLeftChevronDisabled}
                    sx={{ color: 'text.secondary' }}
                    onClick={() => setDisplayedDate((prev) => prev.subtract(1, 'month'))}
                >
                    <ChevronLeftRoundedIcon />
                </IconButton>
                <Typography variant="h6" fontWeight={700}>{monthLabel}</Typography>
                <IconButton
                    size="small"
                    disabled={isCurrentMonth}
                    sx={{ color: 'text.secondary' }}
                    onClick={() => !isCurrentMonth && setDisplayedDate((prev) => prev.add(1, 'month'))}
                >
                    <ChevronRightRoundedIcon />
                </IconButton>
            </Box>

            <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1} mb={1}>
                {weekdayLabels.map((day) => (
                    <Typography key={day} textAlign="center" color="text.secondary" fontWeight={600}>
                        {day}
                    </Typography>
                ))}
            </Box>

            <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1}>
                {monthGrid.map((date, idx) => {
                    if (!date) {
                        return <Box key={`empty-${idx}`} height={42} />;
                    }

                    const key = date.format('YYYY-MM-DD');
                    const isActive = activityDaySet.has(key);
                    const isToday = key === todayKey;
                    const isOlderThan60Days = date.isBefore(cutoffDate, 'day');

                    return (
                        <Box
                            key={key}
                            height={42}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                borderRadius: 1,
                                bgcolor: isActive ? 'rgba(46, 188, 125, 0.12)' : 'transparent',
                                border: isToday ? '1px solid #9adfcc' : '1px solid transparent',
                                opacity: isOlderThan60Days ? 0.4 : 1,
                            }}
                        >
                            <Typography
                                fontWeight={600}
                                color={
                                    isOlderThan60Days ?
                                        'text.secondary' :
                                        isActive ?
                                            'var(--color-primary-500)' :
                                            'text.primary'
                                }
                            >
                                {date.date()}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </PageSection>
    );
};

export default MonthlyCalendarSection;
